/**
 * Order Detail Page - P1 Clean Vertical Layout
 * 
 * Design Principles:
 * - Vertical stacked panels for better line item viewing
 * - Edit button on top left for quick access
 * - Line item add/edit/remove functionality
 * - Shipping address visible and editable
 * - Tax and shipping cost editing
 * - Work order generation and viewing
 * - Quote PDF download
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, orderLifecycleAPI, operationsAPI, crmAPI, taxAPI, paymentsAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useRefetchOnReturn } from '../../hooks';
import { toast } from 'sonner';
import CreatePaymentModal from '../../components/admin/CreatePaymentModal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import EntityActivityPanel from '../../components/admin/EntityActivityPanel';
import DiscountEditor from '../../components/admin/DiscountEditor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft,
  Copy,
  Loader2,
  CheckCircle,
  XCircle,
  Truck,
  Package,
  DollarSign,
  FileText,
  Send,
  Eye,
  ExternalLink,
  AlertTriangle,
  Edit2,
  Save,
  X,
  Download,
  User,
  MapPin,
  Phone,
  Mail,
  Plus,
  Trash2,
  Printer,
  ClipboardList,
  Calendar,
  Clock,
  AlertCircle,
  Store,
  Building2,
  ShoppingCart,
  RefreshCw,
  Bell,
  ChevronDown,
  Receipt,
} from 'lucide-react';

// Import shared components from Detail Page System
import { 
  DetailHeader, 
  ContentPanel,
  StatusPill as SharedStatusPill, 
  STATUS_CONFIGS 
} from '../../components/detail-system';

// Import SendEmailModal
import SendEmailModal from '../../components/admin/SendEmailModal';

// Import WorkOrderEditModal
import WorkOrderEditModal from './WorkOrderEditModal';

// ==============================================
// STATUS CONFIGURATIONS
// ==============================================

const OPS_STATUS_CONFIG = {
  OPEN: { label: 'Open', color: 'bg-blue-100 text-blue-700', icon: Package },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-purple-100 text-purple-700', icon: Loader2 },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const INVOICE_STATUS_CONFIG = {
  NONE: { label: 'Not Issued', color: 'bg-gray-100 text-gray-500' },
  NOT_ISSUED: { label: 'Not Issued', color: 'bg-gray-100 text-gray-500' },
  ISSUED: { label: 'Issued', color: 'bg-blue-100 text-blue-700' },
  SENT: { label: 'Sent', color: 'bg-indigo-100 text-indigo-700', icon: Send },
  PAID: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  VOID: { label: 'Void', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const FULFILLMENT_STATUS_CONFIG = {
  NONE: { label: 'None', color: 'bg-gray-100 text-gray-500' },
  READY: { label: 'Ready', color: 'bg-cyan-100 text-cyan-700', icon: Package },
  SHIPPED: { label: 'Shipped', color: 'bg-blue-100 text-blue-700', icon: Truck },
};

// ==============================================
// HELPER COMPONENTS
// ==============================================

const StatusPill = ({ status, config, onClick, editable = false, testId = '' }) => {
  // Normalize status to uppercase for lookup (DB stores lowercase, config uses uppercase)
  const normalizedStatus = (status || '').toUpperCase();
  const statusConfig = config[normalizedStatus] || config.NONE || { label: status || 'None', color: 'bg-gray-100 text-gray-700' };
  const Icon = statusConfig.icon;
  
  return (
    <button
      onClick={onClick}
      disabled={!editable}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm
        ${statusConfig.color}
        ${editable ? 'cursor-pointer hover:opacity-90 hover:shadow-md' : 'cursor-default'}
      `}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {statusConfig.label}
    </button>
  );
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <button 
      onClick={handleCopy}
      className="p-1 hover:bg-gray-100 rounded transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
    </button>
  );
};

/**
 * DueDateInline - Inline editable due date field with color-coded badge
 * Click to open date picker, shows urgency status
 */
const DueDateInline = ({ dueAt, opsStatus, orderId, onUpdate, showYear = false }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dateValue, setDateValue] = useState('');
  
  // Format date for input field (YYYY-MM-DD)
  useEffect(() => {
    if (dueAt) {
      const d = new Date(dueAt);
      setDateValue(d.toISOString().split('T')[0]);
    } else {
      setDateValue('');
    }
  }, [dueAt]);
  
  // Calculate urgency
  const getUrgencyStyle = () => {
    if (!dueAt) return { badge: 'bg-gray-100 text-gray-500', icon: null };
    
    const dueDate = new Date(dueAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDateNormalized = new Date(dueDate);
    dueDateNormalized.setHours(0, 0, 0, 0);
    
    const diffDays = Math.ceil((dueDateNormalized - today) / (1000 * 60 * 60 * 24));
    const isCompleted = opsStatus === 'COMPLETED';
    
    if (!isCompleted) {
      if (diffDays < 0) {
        return { badge: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle };
      } else if (diffDays <= 7) {
        return { badge: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock };
      }
    }
    return { badge: 'bg-gray-100 text-gray-600', icon: null };
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const newDueAt = dateValue ? new Date(dateValue + 'T00:00:00').toISOString() : null;
      await orderLifecycleAPI.updateDueDate(orderId, newDueAt);
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to update due date:', err);
    } finally {
      setSaving(false);
    }
  };
  
  const handleClear = async () => {
    setSaving(true);
    try {
      await orderLifecycleAPI.updateDueDate(orderId, null);
      setDateValue('');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to clear due date:', err);
    } finally {
      setSaving(false);
    }
  };
  
  const { badge, icon: UrgencyIcon } = getUrgencyStyle();
  
  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="date"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          className="w-36 h-7 text-sm"
          data-testid="due-date-input"
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={handleSave}
          disabled={saving}
          className="h-7 px-2"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 text-green-600" />}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setEditing(false)}
          className="h-7 px-2"
        >
          <X className="w-3 h-3 text-gray-400" />
        </Button>
        {dueAt && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleClear}
            disabled={saving}
            className="h-7 px-2 text-red-500 hover:text-red-700"
            title="Clear due date"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    );
  }
  
  const formattedDate = dueAt 
    ? new Date(dueAt).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        ...(showYear && { year: 'numeric' })
      })
    : null;
  
  return (
    <button
      onClick={() => setEditing(true)}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity ${badge}`}
      data-testid="due-date-badge"
    >
      {UrgencyIcon && <UrgencyIcon className="w-3 h-3" />}
      <Calendar className="w-3 h-3" />
      {formattedDate || 'Set due date'}
    </button>
  );
};

// Panel component removed - using ContentPanel from detail-system

// ==============================================
// MAIN COMPONENT
// ==============================================

const OrderDetailPageClean = ({ orderId, onBack, onNavigateToCustomer }) => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  
  // Form states
  const [customerForm, setCustomerForm] = useState({});
  const [shippingAddress, setShippingAddress] = useState({});
  const [itemsForm, setItemsForm] = useState([]);
  const [pricingForm, setPricingForm] = useState({});
  const [notesForm, setNotesForm] = useState('');
  
  // Ship To Contact state
  const [customerContacts, setCustomerContacts] = useState([]);
  const [selectedShipToContactId, setSelectedShipToContactId] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(false);
  
  // Modal states
  const [showOpsStatusModal, setShowOpsStatusModal] = useState(false);
  const [showFulfillmentModal, setShowFulfillmentModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [pendingShipmentStatus, setPendingShipmentStatus] = useState(null);
  const [trackingNumberInput, setTrackingNumberInput] = useState('');
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [newItem, setNewItem] = useState({ product_name: '', description: '', quantity: 1, unit_price: 0 });
  
  // Cancel order state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  // Inline editable fields - separate state to avoid race conditions with order state
  const [inlineTracking, setInlineTracking] = useState('');
  const [inlinePO, setInlinePO] = useState('');
  const originalTrackingRef = useRef('');
  const originalPORef = useRef('');
  
  // Work orders
  const [workOrders, setWorkOrders] = useState([]);
  const [generatingWorkOrder, setGeneratingWorkOrder] = useState(false);
  
  // P4: Invoice reminder schedule state
  const [reminderSchedule, setReminderSchedule] = useState({
    before_due: [],
    after_due: []
  });
  const [savingReminders, setSavingReminders] = useState(false);
  
  // P1: Sales Tax state
  const [salesTaxes, setSalesTaxes] = useState([]);
  const [selectedSalesTaxId, setSelectedSalesTaxId] = useState('');
  const [taxExempt, setTaxExempt] = useState(false);
  const [netTermsDays, setNetTermsDays] = useState(7);
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payments, setPayments] = useState([]);
  
  // Work Order Edit modal state
  const [showWorkOrderEditModal, setShowWorkOrderEditModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  
  // Work Order Preview modal state
  const [showWorkOrderPreviewModal, setShowWorkOrderPreviewModal] = useState(false);
  const [selectedWorkOrderForPreview, setSelectedWorkOrderForPreview] = useState(null);
  
  // Send Email modal state (unified for all email types)
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [sendEmailType, setSendEmailType] = useState(null); // 'invoice' | 'quote' | 'payment' | 'shipped' | 'pickup'
  const [selectedPaymentForEmail, setSelectedPaymentForEmail] = useState(null); // For payment receipt emails
  
  // Invoice & Quote Preview modal state
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [showQuotePreview, setShowQuotePreview] = useState(false);
  const [invoicePdfLoading, setInvoicePdfLoading] = useState(true);
  const [quotePdfLoading, setQuotePdfLoading] = useState(true);
  const [invoicePdfUrl, setInvoicePdfUrl] = useState(null);
  const [quotePdfUrl, setQuotePdfUrl] = useState(null);
  
  // Payment Receipt Preview modal state
  const [showPaymentPreview, setShowPaymentPreview] = useState(false);
  const [paymentPdfLoading, setPaymentPdfLoading] = useState(true);
  const [paymentPdfUrl, setPaymentPdfUrl] = useState(null);
  const [selectedPaymentForPreview, setSelectedPaymentForPreview] = useState(null);
  
  // Fetch payments for this order's invoice
  const fetchPayments = useCallback(async () => {
    if (!order?.invoice_number) return;
    try {
      const response = await paymentsAPI.getPaymentsForInvoice(order.invoice_number);
      setPayments(response.payments || []);
    } catch (err) {
      console.error('Error fetching payments:', err);
    }
  }, [order?.invoice_number]);
  
  useEffect(() => {
    if (order?.invoice_number) {
      fetchPayments();
    }
  }, [order?.invoice_number, fetchPayments]);
  
  // Fetch order data
  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getOrder(orderId);
      setOrder(response);
      
      // Initialize forms
      setCustomerForm({
        customer_name: response.customer_name || '',
        customer_email: response.customer_email || '',
        customer_phone: response.customer_phone || '',
        shipping_method_type: response.shipping_method_type || 'pickup',
      });
      
      setShippingAddress(response.shipping_address || {
        full_name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'USA',
        phone: '',
      });
      
      setItemsForm(response.items || []);
      
      setPricingForm({
        subtotal: response.subtotal || 0,
        tax: response.tax || 0,
        shipping_cost: response.shipping_cost || 0,
        discount: response.discount || 0,
      });
      
      setNotesForm(response.internal_notes || response.admin_notes || '');
      
      // P4: Initialize reminder schedule from invoice snapshot
      if (response.invoice_snapshot?.reminder_schedule) {
        setReminderSchedule({
          before_due: response.invoice_snapshot.reminder_schedule.before_due || [],
          after_due: response.invoice_snapshot.reminder_schedule.after_due || []
        });
      } else {
        setReminderSchedule({ before_due: [], after_due: [] });
      }
      
      // Fetch work orders
      try {
        const woResponse = await operationsAPI.getWorkOrders({ order_id: orderId });
        setWorkOrders(woResponse.work_orders || []);
      } catch (e) {
        console.log('No work orders found');
      }
      
      // P1: Initialize tax-related state from order
      setTaxExempt(response.tax_exempt || false);
      setNetTermsDays(response.net_terms_days ?? 7);
      setSelectedSalesTaxId(response.sales_tax_id || '');
      
      // Initialize inline editable fields
      const trackingVal = response.tracking_number || '';
      const poVal = response.customer_po_number || '';
      setInlineTracking(trackingVal);
      setInlinePO(poVal);
      originalTrackingRef.current = trackingVal;
      originalPORef.current = poVal;
      
    } catch (err) {
      console.error('Failed to fetch order:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);
  
  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);
  
  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchOrder();
      await fetchPayments();
      // toast.success('Data refreshed');  // Optional - can be noisy
    } finally {
      setRefreshing(false);
    }
  };
  
  // Auto-refetch on tab focus or route return
  useRefetchOnReturn({
    refetch: fetchOrder,
    enabled: !loading && !!orderId,
    refreshing,
    throttleMs: 1500,
  });
  
  // P1: Fetch sales tax rates
  useEffect(() => {
    const loadSalesTaxes = async () => {
      try {
        const response = await taxAPI.listZones(false);
        setSalesTaxes(response.items || []);
      } catch (err) {
        console.error('Failed to load sales taxes:', err);
      }
    };
    loadSalesTaxes();
  }, []);
  
  // Fetch customer contacts when entering edit mode
  const fetchCustomerContacts = useCallback(async (accountId) => {
    if (!accountId) return;
    setLoadingContacts(true);
    try {
      const response = await crmAPI.getContacts({ account_id: accountId, page_size: 50 });
      setCustomerContacts(response.contacts || response.items || []);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setCustomerContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }, []);
  
  // Handle entering edit mode
  const handleEnterEditMode = () => {
    if (order?.account_id) {
      fetchCustomerContacts(order.account_id);
    }
    // Set initial ship-to contact if available
    if (order?.ship_to_contact_id) {
      setSelectedShipToContactId(order.ship_to_contact_id);
    }
    setIsEditing(true);
  };
  
  // Handle ship-to contact selection
  const handleShipToContactChange = (contactId) => {
    setSelectedShipToContactId(contactId);
    
    if (contactId === 'manual') {
      // Clear shipping address for manual entry
      setShippingAddress({
        full_name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'USA',
        phone: '',
      });
      return;
    }
    
    // Find the selected contact and populate shipping address
    const contact = customerContacts.find(c => c.id === contactId);
    if (contact) {
      // Check for shipping_address (single object) or addresses (array)
      const addr = contact.shipping_address || contact.addresses?.find(a => a.is_primary) || contact.addresses?.[0];
      setShippingAddress({
        full_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
        address_line1: addr?.address_line1 || '',
        address_line2: addr?.address_line2 || '',
        city: addr?.city || '',
        state: addr?.state || '',
        zip_code: addr?.zip_code || addr?.postal_code || '',  // Handle both field names
        country: addr?.country || 'USA',
        phone: contact.phone || '',
      });
    }
  };
  
  // Check if invoice blocks editing
  // Per Option C: Allow edits after ISSUED/SENT but show warning. Only lock when PAID.
  const isInvoicePaid = order && ['PAID', 'paid'].includes(order.invoice_status);
  const isInvoiceVoid = order && ['VOID', 'void'].includes(order.invoice_status);
  const isInvoiceSentOrIssued = order && ['ISSUED', 'SENT', 'issued', 'sent'].includes(order.invoice_status);
  // Can edit unless invoice is PAID (void invoices can always be edited)
  const canEdit = !isInvoicePaid || isInvoiceVoid;
  // Show warning if editing after invoice issued/sent
  const showEditWarning = isInvoiceSentOrIssued && !isInvoiceVoid;
  
  // Auto-calculate tax when order loads or tax settings change
  useEffect(() => {
    if (!order || !salesTaxes.length) return;
    
    // Don't auto-calculate if tax exempt
    if (taxExempt) {
      setPricingForm(prev => ({ ...prev, tax: 0 }));
      return;
    }
    
    // Calculate subtotal from items
    const subtotal = (order.items || []).reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 
      0
    );
    
    // Get the tax rate - use selected tax zone or default
    let taxRate = 0;
    if (selectedSalesTaxId) {
      const selectedTax = salesTaxes.find(t => t.id === selectedSalesTaxId);
      taxRate = selectedTax?.rate || 0;
    } else {
      // Use default tax rate
      const defaultTax = salesTaxes.find(t => t.is_default);
      taxRate = defaultTax?.rate || 0;
    }
    
    // Calculate tax
    const calculatedTax = subtotal * taxRate;
    
    // Only update if different (to avoid infinite loops)
    setPricingForm(prev => {
      if (Math.abs((prev.tax || 0) - calculatedTax) > 0.01) {
        return { ...prev, tax: calculatedTax };
      }
      return prev;
    });
  }, [order, salesTaxes, taxExempt, selectedSalesTaxId]);
  
  // Calculate total
  const calculateTotal = () => {
    const subtotal = itemsForm.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0);
    const total = subtotal - (pricingForm.discount || 0) + (pricingForm.tax || 0) + (pricingForm.shipping_cost || 0);
    return { subtotal, total };
  };
  
  // Handlers
  const handleSaveAll = async () => {
    if (!canEdit) {
      alert('Cannot edit after invoice issued. Admin must VOID invoice first.');
      return;
    }
    
    try {
      setSaving(true);
      
      const { subtotal, total } = calculateTotal();
      
      // Update customer info (bill-to only, shipping method)
      const customerUpdate = {
        customer_name: customerForm.customer_name,
        customer_email: customerForm.customer_email,
        customer_phone: customerForm.customer_phone,
        shipping_method_type: customerForm.shipping_method_type,
      };
      
      await orderLifecycleAPI.updateCustomer(orderId, customerUpdate);
      
      // Update ship-to if shipping method is "ship"
      if (customerForm.shipping_method_type === 'ship') {
        if (selectedShipToContactId && selectedShipToContactId !== 'manual') {
          // Use contact - backend will create snapshot
          await orderLifecycleAPI.updateShipTo(orderId, { 
            contactId: selectedShipToContactId 
          });
        } else {
          // Manual entry
          await orderLifecycleAPI.updateShipTo(orderId, {
            manualEntry: {
              name: shippingAddress.full_name,
              address: {
                line1: shippingAddress.address_line1,
                line2: shippingAddress.address_line2,
                city: shippingAddress.city,
                state: shippingAddress.state,
                postal_code: shippingAddress.zip_code,
                country: shippingAddress.country || 'USA'
              },
              phone: shippingAddress.phone,
              email: null,
              delivery_notes: null
            }
          });
        }
      }
      
      // Update items and pricing
      await orderLifecycleAPI.updateItems(orderId, {
        items: itemsForm,
        subtotal,
        tax: pricingForm.tax,
        discount: pricingForm.discount,
        shipping_cost: pricingForm.shipping_cost,
        total,
      });
      
      // Update notes
      await orderLifecycleAPI.updateNotes(orderId, notesForm);
      
      await fetchOrder();
      setIsEditing(false);
      alert('Order saved successfully!');
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleOpsStatusChange = async (newStatus) => {
    try {
      setSaving(true);
      await orderLifecycleAPI.updateOpsStatus(orderId, newStatus);
      await fetchOrder();
      setShowOpsStatusModal(false);
    } catch (err) {
      alert('Failed to update status: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleFulfillmentChange = async (newStatus) => {
    // If changing to SHIPPED and not already SHIPPED, show tracking modal
    if (newStatus === 'SHIPPED' && order.fulfillment_status !== 'SHIPPED') {
      setPendingShipmentStatus(newStatus);
      setTrackingNumberInput(order.tracking_number || '');
      setShowFulfillmentModal(false);
      setShowTrackingModal(true);
      return;
    }
    
    // Otherwise proceed directly
    await executeFulfillmentUpdate(newStatus, null);
  };
  
  const executeFulfillmentUpdate = async (newStatus, trackingNumber) => {
    try {
      setSaving(true);
      await orderLifecycleAPI.updateFulfillmentStatus(orderId, newStatus, trackingNumber || order.tracking_number);
      await fetchOrder();
      setShowFulfillmentModal(false);
      setShowTrackingModal(false);
      setTrackingNumberInput('');
      setPendingShipmentStatus(null);
    } catch (err) {
      alert('Failed to update fulfillment: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleTrackingSave = async () => {
    // If we have a pending status (from Notify Shipped flow), update fulfillment
    if (pendingShipmentStatus) {
      await executeFulfillmentUpdate(pendingShipmentStatus, trackingNumberInput);
    } else {
      // Just saving tracking number from header (no status change)
      try {
        setSaving(true);
        await orderLifecycleAPI.updateFulfillmentStatus(orderId, order.fulfillment_status || 'NONE', trackingNumberInput);
        await fetchOrder();
        setShowTrackingModal(false);
        setTrackingNumberInput('');
      } catch (err) {
        alert('Failed to save tracking number: ' + err.message);
      } finally {
        setSaving(false);
      }
    }
  };
  
  const handleTrackingSkip = async () => {
    if (pendingShipmentStatus) {
      await executeFulfillmentUpdate(pendingShipmentStatus, null);
    } else {
      // Just close modal if opened from header
      setShowTrackingModal(false);
      setTrackingNumberInput('');
    }
  };
  
  // Item handlers
  const handleAddItem = () => {
    if (!newItem.product_name) return;
    setItemsForm([...itemsForm, { ...newItem }]);
    setNewItem({ product_name: '', description: '', quantity: 1, unit_price: 0 });
    setShowAddItemModal(false);
  };
  
  const handleRemoveItem = (index) => {
    setItemsForm(itemsForm.filter((_, i) => i !== index));
  };
  
  const handleUpdateItem = (index, field, value) => {
    const updated = [...itemsForm];
    updated[index] = { ...updated[index], [field]: value };
    setItemsForm(updated);
  };
  
  // Quote handlers
  const handleIssueQuote = async () => {
    try {
      setSaving(true);
      await orderLifecycleAPI.issueQuote(orderId);
      await fetchOrder();
    } catch (err) {
      alert('Failed to issue quote: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleDownloadQuotePdf = () => {
    const url = orderLifecycleAPI.getQuotePdfUrl(orderId);
    window.open(url, '_blank');
  };
  
  // Invoice handlers
  const handleIssueInvoice = async () => {
    try {
      setSaving(true);
      await orderLifecycleAPI.issueInvoice(orderId);
      await fetchOrder();
    } catch (err) {
      alert('Failed to issue invoice: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleVoidInvoice = async () => {
    if (!voidReason.trim()) {
      alert('Please provide a reason for voiding');
      return;
    }
    try {
      setSaving(true);
      await orderLifecycleAPI.voidInvoice(orderId, voidReason);
      await fetchOrder();
      setShowVoidModal(false);
      setVoidReason('');
    } catch (err) {
      alert('Failed to void invoice: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleReissueInvoice = async () => {
    try {
      setSaving(true);
      await orderLifecycleAPI.reissueInvoice(orderId);
      await fetchOrder();
    } catch (err) {
      alert('Failed to re-issue invoice: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleDownloadInvoicePdf = () => {
    const url = orderLifecycleAPI.getInvoicePdfUrl(orderId);
    window.open(url, '_blank');
  };
  
  const handlePreviewInvoicePdf = async () => {
    setInvoicePdfLoading(true);
    setInvoicePdfUrl(null);
    setShowInvoicePreview(true);
    
    try {
      // Fetch PDF as blob with credentials
      const response = await fetch(orderLifecycleAPI.getInvoicePreviewUrl(orderId), {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to load invoice PDF');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setInvoicePdfUrl(blobUrl);
    } catch (err) {
      console.error('Error loading invoice PDF:', err);
      toast.error('Failed to load invoice preview');
    } finally {
      setInvoicePdfLoading(false);
    }
  };
  
  const handlePreviewQuotePdf = async () => {
    setQuotePdfLoading(true);
    setQuotePdfUrl(null);
    setShowQuotePreview(true);
    
    try {
      // Fetch PDF as blob with credentials
      const response = await fetch(orderLifecycleAPI.getQuotePreviewUrl(orderId), {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to load quote PDF');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setQuotePdfUrl(blobUrl);
    } catch (err) {
      console.error('Error loading quote PDF:', err);
      toast.error('Failed to load quote preview');
    } finally {
      setQuotePdfLoading(false);
    }
  };
  
  // Payment Receipt Preview handler
  const handlePreviewPaymentPdf = async (payment) => {
    setPaymentPdfLoading(true);
    setPaymentPdfUrl(null);
    setSelectedPaymentForPreview(payment);
    setShowPaymentPreview(true);
    
    try {
      // Fetch PDF as blob with credentials
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/admin/${payment.id}/pdf?preview=true`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to load payment receipt PDF');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPaymentPdfUrl(blobUrl);
    } catch (err) {
      console.error('Error loading payment PDF:', err);
      toast.error('Failed to load payment receipt preview');
    } finally {
      setPaymentPdfLoading(false);
    }
  };
  
  // Download Payment Receipt PDF
  const handleDownloadPaymentPdf = (payment) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/payments/admin/${payment.id}/pdf`;
    window.open(url, '_blank');
  };
  
  // Open Send Email modal for payment receipts
  const openPaymentEmailModal = (payment) => {
    setSelectedPaymentForEmail(payment);
    setSendEmailType('payment');
    setShowSendEmailModal(true);
  };
  
  // Unified Send Email modal handlers
  const openSendEmailModal = (type) => {
    setSendEmailType(type);
    setSelectedPaymentForEmail(null); // Clear payment selection for non-payment types
    setShowSendEmailModal(true);
  };
  
  const handleSendEmail = async (requestData) => {
    // Call the appropriate API based on email type
    switch (sendEmailType) {
      case 'invoice':
        return await orderLifecycleAPI.sendInvoice(orderId, requestData);
      case 'quote':
        return await orderLifecycleAPI.sendQuote(orderId, requestData);
      case 'payment':
        if (!selectedPaymentForEmail) throw new Error('No payment selected');
        const result = await paymentsAPI.sendReceipt(selectedPaymentForEmail.id, requestData);
        await fetchPayments(); // Refresh payments to update sent status
        return result;
      case 'shipped':
        return await orderLifecycleAPI.sendShippedNotification(orderId, requestData);
      case 'pickup':
        return await orderLifecycleAPI.sendReadyForPickupNotification(orderId, requestData);
      default:
        throw new Error('Unknown email type');
    }
  };
  
  // P4: Reminder schedule handlers
  const toggleReminderDay = (type, day) => {
    setReminderSchedule(prev => {
      const list = prev[type] || [];
      if (list.includes(day)) {
        return { ...prev, [type]: list.filter(d => d !== day) };
      } else {
        return { ...prev, [type]: [...list, day].sort((a, b) => type === 'before_due' ? b - a : a - b) };
      }
    });
  };
  
  const handleSaveReminders = async () => {
    try {
      setSavingReminders(true);
      await orderLifecycleAPI.updateInvoiceReminders(
        orderId, 
        reminderSchedule.before_due, 
        reminderSchedule.after_due
      );
      alert('Reminder schedule saved');
      await fetchOrder();
    } catch (err) {
      alert('Failed to save reminder schedule: ' + err.message);
    } finally {
      setSavingReminders(false);
    }
  };
  
  // Calculate preview dates for reminders based on due_at
  const calculateReminderPreviewDates = () => {
    if (!order?.due_at) return [];
    
    const dueDate = new Date(order.due_at);
    const previews = [];
    
    // Before due date reminders
    for (const days of (reminderSchedule.before_due || []).sort((a, b) => b - a)) {
      const reminderDate = new Date(dueDate);
      reminderDate.setDate(reminderDate.getDate() - days);
      previews.push({
        date: reminderDate,
        label: `${days} day${days > 1 ? 's' : ''} before due date`,
        type: 'before'
      });
    }
    
    // After due date reminders
    for (const days of (reminderSchedule.after_due || []).sort((a, b) => a - b)) {
      const reminderDate = new Date(dueDate);
      reminderDate.setDate(reminderDate.getDate() + days);
      previews.push({
        date: reminderDate,
        label: `${days} day${days > 1 ? 's' : ''} after due date`,
        type: 'after'
      });
    }
    
    return previews.sort((a, b) => a.date - b.date);
  };
  
  // Work Order handlers
  const handleGenerateWorkOrder = async () => {
    try {
      setGeneratingWorkOrder(true);
      await operationsAPI.generateWorkOrder(orderId);
      await fetchOrder();
      toast.success('Work order created successfully!');
    } catch (err) {
      toast.error('Failed to create work order: ' + err.message);
    } finally {
      setGeneratingWorkOrder(false);
    }
  };
  
  const handlePrintWorkOrder = (workOrderId) => {
    // Open work order in new tab for printing
    window.open(`/admin/work-orders/${workOrderId}/print`, '_blank');
  };
  
  // Cancel order handler
  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }
    
    try {
      setCancelling(true);
      await adminAPI.cancelOrder(orderId, { reason: cancelReason.trim() });
      toast.success('Order cancelled successfully');
      setShowCancelModal(false);
      setCancelReason('');
      await fetchOrder();
    } catch (err) {
      toast.error('Failed to cancel order: ' + err.message);
    } finally {
      setCancelling(false);
    }
  };
  
  // Check if order is cancelled (read-only mode)
  const isCancelled = order?.ops_status === 'CANCELLED';
  
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (error || !order) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500">{error || 'Order not found'}</p>
        <Button variant="outline" onClick={onBack} className="mt-4">Go Back</Button>
      </div>
    );
  }
  
  const { subtotal, total } = calculateTotal();
  const invoiceTotal = order.invoice_snapshot?.total || total;
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Option B: Split Header with Info Cards */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        {/* Main Header Row */}
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            {/* Left Side: Navigation + Order ID + Customer */}
            <div className="flex items-center gap-4">
              {/* Navigation Buttons */}
              <div className="flex items-center gap-1">
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={onBack}
                  className="h-9 w-9 rounded-lg border-gray-200 hover:bg-gray-100"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  data-testid="refresh-order-btn"
                  title="Refresh"
                  className="h-9 w-9 rounded-lg border-gray-200 hover:bg-gray-100"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {/* Order ID + Customer */}
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">{order.order_id}</h1>
                  <CopyButton text={order.order_id} />
                  {/* Edit Button - Enhanced */}
                  {canEdit && !isEditing && (
                    <Button 
                      size="sm" 
                      onClick={handleEnterEditMode}
                      className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4 font-semibold shadow-sm"
                      data-testid="edit-order-btn"
                    >
                      <Edit2 className="w-4 h-4 mr-1.5" />
                      Edit Order
                    </Button>
                  )}
                  {/* Cancel Order Button - Enhanced */}
                  {canEdit && !isEditing && !isCancelled && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowCancelModal(true)}
                      className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 h-9 px-4 font-semibold"
                      data-testid="cancel-order-btn"
                    >
                      <XCircle className="w-4 h-4 mr-1.5" />
                      Cancel
                    </Button>
                  )}
                </div>
                {/* Cancelled Badge */}
                {isCancelled && (
                  <Badge className="bg-red-100 text-red-700 text-xs ml-2">
                    CANCELLED
                  </Badge>
                )}
                {order.account_id && onNavigateToCustomer ? (
                  <button
                    onClick={() => onNavigateToCustomer(order.account_id, order.order_id)}
                    className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-sm"
                    data-testid="navigate-to-account-btn"
                  >
                    {order.customer_name || 'No Customer'}
                  </button>
                ) : (
                  <span className="text-gray-500 text-sm">{order.customer_name || 'No Customer'}</span>
                )}
              </div>
              
              {/* Edit Mode Actions */}
              {isEditing && (
                <div className="flex items-center gap-2 ml-4">
                  <Button 
                    size="sm" 
                    onClick={handleSaveAll}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="save-order-btn"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                    <Save className="w-4 h-4 mr-1" />
                    Save All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    data-testid="cancel-edit-btn"
                    onClick={() => {
                      setIsEditing(false);
                      fetchOrder();
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            
            {/* Right Side: Info Cards + Actions */}
            <div className="flex items-center gap-3">
              {/* Total Card - PROMINENT */}
              <div className="flex items-center gap-3 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl px-5 py-3 border-2 border-emerald-200 shadow-sm">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium uppercase tracking-wide">{order.net_terms_days === 0 ? 'Prepay' : `Net ${order.net_terms_days || 7}`}</p>
                  <p className="font-bold text-emerald-900 text-lg" data-testid="order-total">${invoiceTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
              
              {/* Due Date Card - COLOR CODED */}
              {(() => {
                const dueDate = order.due_at ? new Date(order.due_at) : null;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const diffDays = dueDate ? Math.ceil((new Date(dueDate).setHours(0,0,0,0) - today) / (1000 * 60 * 60 * 24)) : null;
                const isCompleted = order.ops_status === 'COMPLETED';
                
                // Color coding logic
                let cardStyle = 'bg-gray-50 border-gray-200';
                let iconBg = 'bg-gray-100';
                let iconColor = 'text-gray-500';
                let textColor = 'text-gray-600';
                let labelColor = 'text-gray-500';
                
                if (dueDate && !isCompleted) {
                  if (diffDays < 0) {
                    // Overdue - Red
                    cardStyle = 'bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-300';
                    iconBg = 'bg-red-100';
                    iconColor = 'text-red-600';
                    textColor = 'text-red-700';
                    labelColor = 'text-red-500';
                  } else if (diffDays <= 3) {
                    // Due very soon - Orange
                    cardStyle = 'bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-300';
                    iconBg = 'bg-orange-100';
                    iconColor = 'text-orange-600';
                    textColor = 'text-orange-700';
                    labelColor = 'text-orange-500';
                  } else if (diffDays <= 7) {
                    // Due soon - Yellow
                    cardStyle = 'bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-300';
                    iconBg = 'bg-yellow-100';
                    iconColor = 'text-yellow-600';
                    textColor = 'text-yellow-700';
                    labelColor = 'text-yellow-600';
                  } else {
                    // Due later - Blue
                    cardStyle = 'bg-gradient-to-br from-blue-50 to-sky-50 border-2 border-blue-200';
                    iconBg = 'bg-blue-100';
                    iconColor = 'text-blue-600';
                    textColor = 'text-blue-700';
                    labelColor = 'text-blue-500';
                  }
                }
                
                return (
                  <div className={`flex items-center gap-3 rounded-xl px-5 py-3 shadow-sm ${cardStyle}`}>
                    <div className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center`}>
                      <Clock className={`w-5 h-5 ${iconColor}`} />
                    </div>
                    <div>
                      <p className={`text-xs font-medium uppercase tracking-wide ${labelColor}`}>
                        {diffDays !== null && diffDays < 0 ? 'OVERDUE' : diffDays !== null && diffDays <= 3 ? 'DUE SOON' : 'Due Date'}
                      </p>
                      <DueDateInline 
                        dueAt={order.due_at} 
                        opsStatus={order.ops_status}
                        orderId={order.order_id}
                        onUpdate={fetchOrder}
                        className={`font-bold text-base ${textColor}`}
                        showYear={true}
                      />
                    </div>
                  </div>
                );
              })()}
              
              {/* Created Date Card - Secondary */}
              <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-2.5 border border-gray-200 shadow-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500 font-medium">Created</p>
                  <p className="font-semibold text-gray-700 text-sm" data-testid="order-date">
                    {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                </div>
              </div>
              
              {/* Record Payment Button - PROMINENT */}
              {order.invoice_number && ['ISSUED', 'SENT', 'issued', 'sent'].includes(order.invoice_status) && (
                <Button 
                  className="bg-green-600 hover:bg-green-700 text-white h-12 px-6 font-bold text-base shadow-lg hover:shadow-xl transition-all"
                  onClick={() => setShowPaymentModal(true)}
                  data-testid="header-record-payment-btn"
                >
                  <DollarSign className="w-5 h-5 mr-2" /> Record Payment
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {/* Status Strip - Enhanced */}
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-slate-50 border-t border-gray-200">
          <div className="flex items-center gap-5 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600 uppercase font-bold tracking-wider">Status:</span>
              <StatusPill 
                status={order.ops_status || 'OPEN'} 
                config={OPS_STATUS_CONFIG}
                onClick={() => setShowOpsStatusModal(true)}
                editable
                testId="ops-status-pill"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600 uppercase font-bold tracking-wider">Invoice:</span>
              <StatusPill 
                status={order.invoice_status || 'NOT_ISSUED'} 
                config={INVOICE_STATUS_CONFIG}
                testId="invoice-status-pill"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600 uppercase font-bold tracking-wider">Fulfillment:</span>
              <StatusPill 
                status={order.fulfillment_status || 'NONE'} 
                config={FULFILLMENT_STATUS_CONFIG}
                onClick={() => setShowFulfillmentModal(true)}
                editable
                testId="fulfillment-status-pill"
              />
            </div>
            
            {/* Separator */}
            <div className="h-8 w-px bg-gray-300 mx-2"></div>
            
            {/* Notify Dropdown - Right after Fulfillment */}
            {(() => {
              // Determine if notification was sent based on shipping method
              const isShipOrder = !order.shipping_method_type || order.shipping_method_type === 'ship' || order.shipping_method_type === 'shipping' || order.shipping_method_type === 'delivery';
              const isPickupOrder = order.shipping_method_type === 'pickup' || order.shipping_method_type === 'customer_pickup';
              const wasNotified = isShipOrder 
                ? !!order.shipped_notification_sent_at 
                : isPickupOrder 
                  ? !!order.pickup_notification_sent_at 
                  : false;
              
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant={wasNotified ? "default" : "outline"}
                      className={`gap-2 h-10 px-4 rounded-lg font-semibold transition-all ${wasNotified ? 'bg-green-600 hover:bg-green-700 text-white shadow-sm' : 'bg-white hover:bg-blue-50 border-2 border-blue-200 text-blue-700 hover:border-blue-300'}`}
                    >
                      <Bell className="w-4 h-4" />
                      {wasNotified ? 'Notified ✓' : 'Notify Customer'}
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {isShipOrder && (
                      <DropdownMenuItem 
                        onClick={() => {
                          if (order.tracking_number) {
                            openSendEmailModal('shipped');
                          } else {
                            // Set pending status so modal knows to mark as shipped after saving
                            setPendingShipmentStatus('SHIPPED');
                            setTrackingNumberInput('');
                            setShowTrackingModal(true);
                          }
                        }}
                      >
                        <Truck className="w-4 h-4 mr-2" />
                        {order.shipped_notification_sent_at ? 'Resend Shipped' : 'Order Shipped'}
                      </DropdownMenuItem>
                    )}
                    {(isPickupOrder || !order.shipping_method_type) && (
                      <DropdownMenuItem onClick={() => openSendEmailModal('pickup')}>
                        <Store className="w-4 h-4 mr-2" />
                        {order.pickup_notification_sent_at ? 'Resend Pickup' : 'Ready for Pickup'}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}
            
            {/* Tracking # - Enhanced Inline Editable */}
            <div className="flex items-center gap-2 bg-white border-2 border-gray-200 rounded-lg px-4 py-2 hover:border-gray-300 transition-colors">
              <Truck className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-500 font-semibold uppercase">Tracking:</span>
              <input
                type="text"
                className="text-sm font-mono text-gray-700 bg-transparent border-none focus:outline-none w-32 placeholder-gray-400 font-medium"
                placeholder="+ Add"
                value={inlineTracking}
                onChange={(e) => setInlineTracking(e.target.value)}
                onBlur={async (e) => {
                  const newTracking = e.target.value.trim();
                  if (newTracking !== originalTrackingRef.current) {
                      try {
                        await orderLifecycleAPI.updateFulfillmentStatus(
                          orderId, 
                          order.fulfillment_status || 'NONE', 
                          newTracking || null
                        );
                        originalTrackingRef.current = newTracking;
                        toast.success(newTracking ? 'Tracking # saved' : 'Tracking # cleared');
                        await fetchOrder();
                      } catch (err) {
                        console.error('Failed to save tracking:', err);
                        toast.error('Failed to save tracking #');
                        // Revert to original on error
                        setInlineTracking(originalTrackingRef.current);
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    }
                  }}
                  data-testid="tracking-number-input"
                />
                {inlineTracking && <CopyButton text={inlineTracking} />}
            </div>
            
            {/* PO# - Enhanced Inline Editable */}
            <div className="flex items-center gap-2 bg-white border-2 border-gray-200 rounded-lg px-4 py-2 hover:border-gray-300 transition-colors">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-500 font-semibold uppercase">PO#:</span>
              <input
                type="text"
                className="text-sm font-medium text-gray-700 bg-transparent border-none focus:outline-none w-24 placeholder-gray-400"
                placeholder="+ Add"
                value={inlinePO}
                onChange={(e) => setInlinePO(e.target.value)}
                onBlur={async (e) => {
                  const newPO = e.target.value.trim();
                  if (newPO !== originalPORef.current) {
                    try {
                      await adminAPI.updateOrder(orderId, { customer_po_number: newPO || null });
                      originalPORef.current = newPO;
                      toast.success(newPO ? 'PO # saved' : 'PO # cleared');
                      await fetchOrder();
                    } catch (err) {
                      console.error('Failed to save PO#:', err);
                      toast.error('Failed to save PO #');
                      // Revert to original on error
                      setInlinePO(originalPORef.current);
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                }}
                data-testid="po-number-input"
              />
              {inlinePO && <CopyButton text={inlinePO} />}
            </div>
            
            {/* Print Dropdown - Enhanced */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="gap-2 h-10 px-4 rounded-lg bg-white hover:bg-gray-50 border-2 border-gray-300 font-semibold text-gray-700 hover:border-gray-400 transition-all"
                  data-testid="print-dropdown-btn"
                >
                  <Printer className="w-4 h-4" />
                  Print
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs text-gray-500">Documents</DropdownMenuLabel>
                <DropdownMenuItem 
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/print/orders/${orderId}/summary`, '_blank')}
                  data-testid="print-order-summary"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Order Summary
                </DropdownMenuItem>
                {workOrders.length > 0 && (
                  <DropdownMenuItem 
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/print/work-orders/${workOrders[0]?.id || workOrders[0]?.work_order_id}/document`, '_blank')}
                    data-testid="print-work-order"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Work Order
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/print/orders/${orderId}/packing-slip`, '_blank')}
                  data-testid="print-packing-slip"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Packing Slip
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-gray-500">Financials</DropdownMenuLabel>
                {order.invoice_id && (
                  <DropdownMenuItem 
                    onClick={async () => {
                      try {
                        const pdfUrl = await orderLifecycleAPI.getInvoicePreviewUrl(orderId);
                        window.open(pdfUrl, '_blank');
                      } catch (e) {
                        toast.error('Invoice not available');
                      }
                    }}
                  >
                    <Receipt className="w-4 h-4 mr-2" />
                    Invoice
                  </DropdownMenuItem>
                )}
                {order.quote_id && (
                  <DropdownMenuItem 
                    onClick={async () => {
                      try {
                        const pdfUrl = await orderLifecycleAPI.getQuotePreviewUrl(orderId);
                        window.open(pdfUrl, '_blank');
                      } catch (e) {
                        toast.error('Quote not available');
                      }
                    }}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Quote
                  </DropdownMenuItem>
                )}
                {(!order.invoice_id && !order.quote_id) && (
                  <DropdownMenuItem disabled className="text-gray-400">
                    No invoice/quote issued
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/print/orders/${orderId}/all`, '_blank')}
                  className="font-medium"
                  data-testid="print-all"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print All
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Tax Exempt badge */}
            {order.tax_exempt && (
              <Badge 
                className="bg-green-100 text-green-700 text-xs"
                data-testid="tax-exempt-badge"
              >
                Tax Exempt
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {/* Main Content - MODERN 2-COLUMN LAYOUT (like Vendor/PO pages) */}
      <div className="p-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT COLUMN - Main Content (2/3 width on large screens) */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Panel 1: Bill-To & Ship-To - Immutable Address Snapshots */}
            <ContentPanel 
          title="Bill-To & Ship-To" 
          icon={Building2}
          locked={isInvoicePaid && !isInvoiceVoid}
          lockedMessage={isInvoicePaid ? "Invoice is PAID — address fields locked." : showEditWarning ? "⚠️ Invoice has been issued. Changes may require re-issuing." : ""}
          testId="bill-to-ship-to-panel"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bill-To Section */}
            <div className="space-y-4" data-testid="bill-to-section">
              <h4 className="font-medium text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Bill To
              </h4>
              {isEditing ? (
                <>
                  <div>
                    <Label>Customer Name</Label>
                    <Input 
                      value={customerForm.customer_name}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, customer_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input 
                      type="email"
                      value={customerForm.customer_email}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, customer_email: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input 
                      value={customerForm.customer_phone}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Fulfillment Method</Label>
                    <Select 
                      value={customerForm.shipping_method_type}
                      onValueChange={(v) => setCustomerForm(prev => ({ ...prev, shipping_method_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pickup">Pickup</SelectItem>
                        <SelectItem value="ship">Ship</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <div className="space-y-3 text-sm">
                  {/* Render from bill_to_snapshot if available, else fallback to legacy */}
                  {order.bill_to_snapshot ? (
                    <>
                      <div className="flex items-start gap-2">
                        <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div>
                          <p className="font-medium">{order.bill_to_snapshot.company_name || order.customer_name || '—'}</p>
                          {order.bill_to_snapshot.attention && (
                            <p className="text-gray-500 text-xs">Attn: {order.bill_to_snapshot.attention}</p>
                          )}
                        </div>
                      </div>
                      {order.bill_to_snapshot.address && (
                        <div className="flex items-start gap-2 pl-6">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                          <div>
                            <p>{order.bill_to_snapshot.address.address_line1}</p>
                            {order.bill_to_snapshot.address.address_line2 && (
                              <p>{order.bill_to_snapshot.address.address_line2}</p>
                            )}
                            <p>
                              {order.bill_to_snapshot.address.city}, {order.bill_to_snapshot.address.state} {order.bill_to_snapshot.address.zip_code}
                            </p>
                          </div>
                        </div>
                      )}
                      {order.bill_to_snapshot.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span>{order.bill_to_snapshot.email}</span>
                        </div>
                      )}
                      {order.bill_to_snapshot.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{order.bill_to_snapshot.phone}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Fallback to legacy customer fields */
                    <>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{order.customer_name || '—'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span>{order.customer_email || '—'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span>{order.customer_phone || '—'}</span>
                      </div>
                    </>
                  )}
                  <div className="flex items-center gap-2 pt-2 border-t mt-2">
                    {order.fulfillment_method === 'PICKUP' || order.shipping_method_type === 'pickup' ? (
                      <Store className="w-4 h-4 text-green-500" />
                    ) : (
                      <Truck className="w-4 h-4 text-blue-500" />
                    )}
                    <span className="capitalize font-medium">
                      {order.fulfillment_method === 'PICKUP' || order.shipping_method_type === 'pickup' ? 'Customer Pickup' : 'Ship to Customer'}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Ship-To Section */}
            <div className="space-y-4" data-testid="ship-to-section">
              <h4 className="font-medium text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
                <Truck className="w-4 h-4" /> Ship To
              </h4>
              {isEditing ? (
                /* Edit mode - different UI based on fulfillment method */
                customerForm.shipping_method_type === 'pickup' ? (
                  /* Pickup - show simple message */
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                      <Store className="w-5 h-5 text-green-600" />
                      <p className="text-green-800 font-medium">Customer Pickup Selected</p>
                    </div>
                    <p className="text-sm text-green-600 mt-1">No shipping address required.</p>
                  </div>
                ) : (
                  /* Ship - show contact selector and address fields */
                  <>
                    {/* Ship To Contact Selector */}
                    <div>
                      <Label>Select Ship-To Contact</Label>
                      <Select
                        value={selectedShipToContactId || 'manual'}
                        onValueChange={handleShipToContactChange}
                        disabled={loadingContacts}
                      >
                        <SelectTrigger data-testid="ship-to-contact-select">
                          <SelectValue placeholder={loadingContacts ? "Loading contacts..." : "Select contact or enter manually"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Enter address manually</SelectItem>
                          {customerContacts.map(contact => {
                            const addr = contact.shipping_address || contact.addresses?.[0];
                            return (
                              <SelectItem key={contact.id} value={contact.id}>
                                {contact.first_name} {contact.last_name}
                                {contact.is_default_ship_to && ' (Default Ship-To)'}
                                {addr?.city && ` - ${addr.city}, ${addr.state}`}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {customerContacts.length === 0 && !loadingContacts && (
                        <p className="text-xs text-amber-600 mt-1">No contacts found for this customer. Enter address manually.</p>
                      )}
                    </div>
                    
                    {/* Address Fields */}
                    <div className="border-t pt-4 mt-4">
                      <Label className="text-xs text-gray-500 uppercase mb-2 block">Shipping Address</Label>
                      <div className="space-y-3">
                        <div>
                          <Label>Full Name</Label>
                          <Input 
                            value={shippingAddress.full_name || ''}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, full_name: e.target.value }))}
                            data-testid="ship-to-full-name"
                          />
                        </div>
                        <div>
                          <Label>Address Line 1</Label>
                          <Input 
                            value={shippingAddress.address_line1 || ''}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, address_line1: e.target.value }))}
                            data-testid="ship-to-address1"
                          />
                        </div>
                        <div>
                          <Label>Address Line 2</Label>
                          <Input 
                            value={shippingAddress.address_line2 || ''}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, address_line2: e.target.value }))}
                            data-testid="ship-to-address2"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label>City</Label>
                            <Input 
                              value={shippingAddress.city || ''}
                              onChange={(e) => setShippingAddress(prev => ({ ...prev, city: e.target.value }))}
                              data-testid="ship-to-city"
                            />
                          </div>
                          <div>
                            <Label>State</Label>
                            <Input 
                              value={shippingAddress.state || ''}
                              onChange={(e) => setShippingAddress(prev => ({ ...prev, state: e.target.value }))}
                              data-testid="ship-to-state"
                            />
                          </div>
                          <div>
                            <Label>ZIP</Label>
                            <Input 
                              value={shippingAddress.zip_code || ''}
                              onChange={(e) => setShippingAddress(prev => ({ ...prev, zip_code: e.target.value }))}
                              data-testid="ship-to-zip"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input 
                            value={shippingAddress.phone || ''}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, phone: e.target.value }))}
                            data-testid="ship-to-phone"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )
              ) : (
                <div className="space-y-1 text-sm">
                  {/* Check fulfillment method - PICKUP shows message, SHIP shows address */}
                  {order.fulfillment_method === 'PICKUP' || order.shipping_method_type === 'pickup' ? (
                    <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                      <Store className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Customer Pickup</p>
                        <p className="text-sm text-green-600">Customer will pick up at location.</p>
                      </div>
                    </div>
                  ) : order.ship_to_snapshot ? (
                    /* Render from ship_to_snapshot (immutable, normalized) */
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-blue-500 mt-0.5" />
                        <div>
                          <p className="font-medium">{order.ship_to_snapshot.name || order.ship_to_snapshot.contact_name || '—'}</p>
                          {order.ship_to_snapshot.address && (
                            <>
                              <p>{order.ship_to_snapshot.address.line1 || order.ship_to_snapshot.address.address_line1}</p>
                              {(order.ship_to_snapshot.address.line2 || order.ship_to_snapshot.address.address_line2) && (
                                <p>{order.ship_to_snapshot.address.line2 || order.ship_to_snapshot.address.address_line2}</p>
                              )}
                              <p>
                                {order.ship_to_snapshot.address.city}, {order.ship_to_snapshot.address.state} {order.ship_to_snapshot.address.postal_code || order.ship_to_snapshot.address.zip_code}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      {order.ship_to_snapshot.phone && (
                        <div className="flex items-center gap-2 pl-6 text-gray-500">
                          <Phone className="w-3 h-3" />
                          <span>{order.ship_to_snapshot.phone}</span>
                        </div>
                      )}
                      {order.ship_to_snapshot.email && (
                        <div className="flex items-center gap-2 pl-6 text-gray-500">
                          <Mail className="w-3 h-3" />
                          <span>{order.ship_to_snapshot.email}</span>
                        </div>
                      )}
                      {order.ship_to_snapshot.delivery_notes && (
                        <div className="mt-2 p-2 bg-amber-50 rounded text-amber-700 text-xs">
                          <span className="font-medium">Delivery Notes:</span> {order.ship_to_snapshot.delivery_notes}
                        </div>
                      )}
                    </div>
                  ) : order.shipping_address ? (
                    /* Fallback to legacy shipping_address */
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="font-medium">{order.shipping_address.full_name || order.customer_name}</p>
                        <p>{order.shipping_address.address_line1}</p>
                        {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                        <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
                        {order.shipping_address.phone && <p className="text-gray-500">{order.shipping_address.phone}</p>}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-400 italic">No shipping address set</p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Tracking Number */}
          {order.tracking_number && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm">
                <Package className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">Tracking:</span>
                <span className="font-mono">{order.tracking_number}</span>
              </div>
            </div>
          )}
        </ContentPanel>
        
        {/* Panel 2: Line Items & Pricing - Full Width for better viewing */}
        <ContentPanel 
          title="Line Items & Pricing" 
          icon={DollarSign}
          locked={isInvoicePaid && !isInvoiceVoid}
          lockedMessage={isInvoicePaid ? "Invoice is PAID — billing fields locked." : showEditWarning ? "⚠️ Invoice has been issued. Changes may require re-issuing." : ""}
          testId="line-items-pricing-panel"
          actions={isEditing && canEdit && (
            <Button size="sm" variant="outline" onClick={() => setShowAddItemModal(true)} data-testid="add-item-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Item
            </Button>
          )}
        >
          {/* Line Items Table */}
          <div className="border rounded-lg overflow-hidden mb-4">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 text-sm">Item</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600 text-sm w-24">Qty</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 text-sm w-32">Unit Price</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 text-sm w-32">Total</th>
                  {isEditing && <th className="px-4 py-3 w-16"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {itemsForm.length === 0 ? (
                  <tr>
                    <td colSpan={isEditing ? 5 : 4} className="px-4 py-8 text-center text-gray-400">
                      No items yet. {isEditing && 'Click "Add Item" to add products.'}
                    </td>
                  </tr>
                ) : (
                  itemsForm.map((item, idx) => (
                    <tr key={idx} className="align-top">
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="space-y-2">
                            <Input 
                              value={item.product_name || ''}
                              onChange={(e) => handleUpdateItem(idx, 'product_name', e.target.value)}
                              className="w-full"
                              placeholder="Product name"
                            />
                            <Textarea 
                              value={item.description || ''}
                              onChange={(e) => handleUpdateItem(idx, 'description', e.target.value)}
                              className="w-full text-sm"
                              placeholder="Description (optional, multi-line)"
                              rows={2}
                            />
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium">{item.product_name || 'Item'}</p>
                            {item.description && (
                              <p className="text-sm text-gray-600 whitespace-pre-wrap mt-1">{item.description}</p>
                            )}
                            {item.sku && <p className="text-xs text-gray-400 mt-1">SKU: {item.sku}</p>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isEditing ? (
                          <Input 
                            type="number"
                            min="1"
                            value={item.quantity || 1}
                            onChange={(e) => handleUpdateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                            className="w-20 text-center mx-auto"
                          />
                        ) : (
                          item.quantity || 1
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <Input 
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unit_price || 0}
                            onChange={(e) => handleUpdateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="w-28 text-right ml-auto"
                          />
                        ) : (
                          `$${(item.unit_price || 0).toFixed(2)}`
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ${((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}
                      </td>
                      {isEditing && (
                        <td className="px-4 py-3 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRemoveItem(idx)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-80 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              
              {/* Wave-style Discount Editor */}
              <DiscountEditor
                orderId={orderId}
                subtotal={subtotal}
                discountConfig={
                  // Map backend fields to DiscountEditor format
                  order?.discount_config || (order?.discount_type ? {
                    type: order.discount_type === 'percentage' ? 'PERCENT' : 'AMOUNT',
                    value: order.discount_value || 0
                  } : null)
                }
                discountAmount={order?.discount_amount || order?.discount || 0}
                discountDescription={order?.discount_description}
                isLocked={isInvoicePaid && !isInvoiceVoid}
                onSave={async (type, value, description) => {
                  await orderLifecycleAPI.updateDiscount(orderId, type, value, description);
                }}
                onUpdate={fetchOrder}
              />
              
              {/* P1: Sales Tax - Dropdown selector */}
              <div className="flex justify-between text-sm items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Sales Tax</span>
                  {taxExempt && (
                    <Badge className="bg-green-100 text-green-700 text-xs">Exempt</Badge>
                  )}
                </div>
                {taxExempt ? (
                  <span className="text-gray-400">$0.00</span>
                ) : canEdit ? (
                  <div className="flex items-center gap-2">
                    <Select
                      value={selectedSalesTaxId || 'default'}
                      onValueChange={async (value) => {
                        setSelectedSalesTaxId(value === 'default' ? '' : value);
                        // Calculate tax based on selected rate (optimistic update)
                        const selectedTax = salesTaxes.find(t => t.id === value);
                        const rate = selectedTax?.rate || salesTaxes.find(t => t.is_default)?.rate || 0;
                        const taxAmount = subtotal * rate;
                        setPricingForm(prev => ({ ...prev, tax: taxAmount }));
                        
                        // Persist to backend
                        try {
                          await orderLifecycleAPI.updateSalesTax(orderId, {
                            sales_tax_id: value === 'default' ? '' : value,
                          });
                        } catch (err) {
                          console.error('Failed to update sales tax:', err);
                        }
                      }}
                      data-testid="sales-tax-select"
                    >
                      <SelectTrigger className="w-40 h-8 text-xs">
                        <SelectValue placeholder="Select tax" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">
                          {salesTaxes.find(t => t.is_default)?.display_name || 'Default'} 
                          ({((salesTaxes.find(t => t.is_default)?.rate || 0) * 100).toFixed(2)}%)
                        </SelectItem>
                        {salesTaxes.filter(t => !t.is_default).map(tax => (
                          <SelectItem key={tax.id} value={tax.id}>
                            {tax.display_name || `${tax.city || tax.name}, ${tax.state}`} ({(tax.rate * 100).toFixed(2)}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="font-medium">${(pricingForm.tax || 0).toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      {order.tax_zone_name || 'Default'} ({((order.tax_rate || 0) * 100).toFixed(2)}%)
                    </span>
                    <span className="font-medium">${(pricingForm.tax || 0).toFixed(2)}</span>
                  </div>
                )}
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className="text-gray-500">Shipping</span>
                {isEditing ? (
                  <Input 
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricingForm.shipping_cost || 0}
                    onChange={(e) => setPricingForm(prev => ({ ...prev, shipping_cost: parseFloat(e.target.value) || 0 }))}
                    className="w-28 text-right"
                  />
                ) : (
                  <span>${(pricingForm.shipping_cost || 0).toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </ContentPanel>
        
            {/* Work Orders Panel - Under Line Items */}
            <ContentPanel 
              title="Work Orders" 
              icon={ClipboardList}
              testId="work-orders-panel"
              actions={
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={handleGenerateWorkOrder}
                  disabled={generatingWorkOrder}
                  data-testid="generate-work-order-btn"
                >
                  {generatingWorkOrder && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                  <Plus className="w-3 h-3 mr-1" /> Generate
                </Button>
              }
            >
              {workOrders.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No work orders yet</p>
                  <p className="text-xs mt-1">Work orders are auto-created with new orders</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {workOrders.map((wo) => (
                    <div 
                      key={wo.id} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer border border-gray-200"
                      onClick={() => window.open(`/admin/work-orders/${wo.id || wo.work_order_id}`, '_blank')}
                    >
                      <div className="flex-1 grid grid-cols-4 gap-4 items-center">
                        {/* WO Number */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase">WO #</p>
                          <p className="font-medium text-sm">{wo.work_order_number || wo.id || wo.work_order_id}</p>
                        </div>
                        {/* Description */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Description</p>
                          <p className="text-sm text-gray-700 truncate">{wo.instructions || wo.description || 'Order fulfillment'}</p>
                        </div>
                        {/* Department */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Department</p>
                          <p className="text-sm text-gray-700">{wo.department || '—'}</p>
                        </div>
                        {/* Due Date */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Due Date</p>
                          <p className="text-sm text-gray-700">
                            {wo.due_at ? new Date(wo.due_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedWorkOrder(wo);
                            setShowWorkOrderEditModal(true);
                          }}
                          data-testid="edit-work-order-btn"
                          title="Edit Work Order"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Open preview in modal-like new tab
                            setSelectedWorkOrderForPreview(wo);
                            setShowWorkOrderPreviewModal(true);
                          }}
                          data-testid="preview-work-order-btn"
                          title="Preview Work Order"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`${process.env.REACT_APP_BACKEND_URL}/api/print/work-orders/${wo.id || wo.work_order_id}/document`, '_blank');
                          }}
                          data-testid="print-work-order-btn"
                          title="Print Work Order"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ContentPanel>
        
            {/* Internal Notes */}
            <ContentPanel title="Internal Notes" icon={FileText} testId="internal-notes-panel">
              {isEditing ? (
                <Textarea 
                  value={notesForm}
                  onChange={(e) => setNotesForm(e.target.value)}
                  rows={4}
                  placeholder="Add internal notes..."
                />
              ) : (
                <p className="text-sm text-gray-600 whitespace-pre-wrap min-h-[60px]">
                  {order.internal_notes || order.admin_notes || 'No notes'}
                </p>
              )}
            </ContentPanel>
            
          </div>
          {/* END LEFT COLUMN */}
          
          {/* RIGHT COLUMN - Summary, Details, Activity (1/3 width on large screens) */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Summary Card - Financial Overview + Actions */}
            <div className="bg-white rounded-xl border p-6" data-testid="order-summary-card">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardList className="w-5 h-5 text-gray-600" />
                {!(isInvoicePaid && !isInvoiceVoid) ? (
                  <input
                    type="text"
                    className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-0 placeholder-gray-400 w-full"
                    placeholder="Order description..."
                    value={order.order_summary || ''}
                    onChange={(e) => setOrder(prev => ({ ...prev, order_summary: e.target.value }))}
                    onBlur={async () => {
                      if (order.order_summary !== undefined) {
                        try {
                          await orderLifecycleAPI.updateSummary(orderId, order.order_summary);
                        } catch (err) {
                          console.error('Failed to save summary:', err);
                        }
                      }
                    }}
                    data-testid="order-summary-input"
                  />
                ) : (
                  <h2 className="text-lg font-semibold">{order.order_summary || 'Order Summary'}</h2>
                )}
              </div>
              
              {/* Financial Summary */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="tabular-nums">${subtotal.toFixed(2)}</span>
                </div>
                {(order?.discount_amount || order?.discount || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">
                      Discount
                      {order?.discount_description && (
                        <span className="text-xs text-gray-400 ml-1">({order.discount_description})</span>
                      )}
                    </span>
                    <span className="tabular-nums text-green-600">-${(order?.discount_amount || order?.discount || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Tax</span>
                  <span className="tabular-nums">${(pricingForm.tax || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Shipping</span>
                  <span className="tabular-nums">${(pricingForm.shipping_cost || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t text-base font-bold">
                  <span>Total</span>
                  <span className="tabular-nums">${total.toFixed(2)}</span>
                </div>
                
                {/* Payment Status - only show if invoice exists */}
                {order.invoice_number && (
                  <>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-gray-500">Paid</span>
                      <span className="tabular-nums text-green-600">
                        ${((order.amount_paid_cents || 0) / 100).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span className="text-gray-700">Balance Due</span>
                      <span className={`tabular-nums ${
                        (order.total * 100 - (order.amount_paid_cents || 0)) <= 0 
                          ? 'text-green-600' 
                          : 'text-orange-600'
                      }`}>
                        ${((order.total * 100 - (order.amount_paid_cents || 0)) / 100).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
              </div>
              
              {/* Compact Invoice & Quote Rows */}
              <div className="mt-4 pt-4 border-t space-y-3">
                {/* Invoice Row */}
                {order.invoice_number ? (
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{order.invoice_number}</span>
                        <Badge className={`text-xs ${
                          order.invoice_status === 'PAID' ? 'bg-green-100 text-green-700' :
                          order.invoice_status === 'VOID' ? 'bg-red-100 text-red-700' :
                          order.invoice_status === 'SENT' ? 'bg-green-100 text-green-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {order.invoice_status === 'SENT' ? '✓ SENT' : order.invoice_status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handlePreviewInvoicePdf} title="Preview">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleDownloadInvoicePdf} title="Download PDF">
                          <Download className="w-4 h-4" />
                        </Button>
                        {order.invoice_status === 'ISSUED' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openSendEmailModal('invoice')} disabled={saving}>
                            Send
                          </Button>
                        )}
                        {['SENT', 'sent', 'PAID', 'paid'].includes(order.invoice_status) && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-500" onClick={() => openSendEmailModal('invoice')} disabled={saving} title="Resend invoice email">
                            Resend
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Warning if order modified after invoice sent */}
                    {['SENT', 'sent'].includes(order.invoice_status) && order.updated_at && order.invoice_sent_at && new Date(order.updated_at) > new Date(order.invoice_sent_at) && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-2 py-1.5 rounded">
                        <AlertTriangle className="w-3 h-3" />
                        <span>Order modified after invoice sent. Consider re-issuing if amounts changed.</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">No invoice</span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={handleIssueInvoice}
                      disabled={saving || !order.customer_email || !order.items?.length}
                    >
                      Issue Invoice
                    </Button>
                  </div>
                )}
                
                {/* Quote Row - Hide if no quote AND invoice is PAID (reduces clutter) */}
                {order.quote_number ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{order.quote_number}</span>
                      <Badge className={`text-xs ${
                        ['approved', 'APPROVED'].includes(order.quote_status) ? 'bg-green-100 text-green-700' :
                        ['sent', 'SENT'].includes(order.quote_status) ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {['sent', 'SENT'].includes(order.quote_status) ? '✓ SENT' : order.quote_status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handlePreviewQuotePdf} title="Preview">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleDownloadQuotePdf} title="Download PDF">
                        <Download className="w-4 h-4" />
                      </Button>
                      {['issued', 'ISSUED'].includes(order.quote_status) && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openSendEmailModal('quote')} disabled={saving}>
                          Send
                        </Button>
                      )}
                      {['sent', 'SENT'].includes(order.quote_status) && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-500" onClick={() => openSendEmailModal('quote')} disabled={saving} title="Resend quote email">
                          Resend
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  // Only show "Issue Quote" if invoice is NOT paid (no point issuing quote for paid orders)
                  order.invoice_status !== 'PAID' && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">No quote</span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={handleIssueQuote}
                        disabled={saving || !order.customer_email || !order.items?.length}
                      >
                        Issue Quote
                      </Button>
                    </div>
                  )
                )}
                
                {/* Payments List - show after Quote section */}
                {payments && payments.length > 0 && (
                  <div className="pt-3 border-t">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Payments</p>
                    <div className="space-y-2">
                      {payments.map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between text-sm bg-green-50/50 rounded-lg px-2 py-1.5">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs">{payment.payment_number || payment.id}</span>
                            <Badge className="text-xs bg-green-100 text-green-700">
                              ${((payment.amount_cents || 0) / 100).toFixed(2)}
                            </Badge>
                            {payment.receipt_sent_at && (
                              <Badge className="text-xs bg-blue-100 text-blue-700">
                                <Send className="w-2 h-2 mr-1" />SENT
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0" 
                              onClick={() => handlePreviewPaymentPdf(payment)}
                              title="Preview Receipt"
                              data-testid={`preview-payment-${payment.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0" 
                              onClick={() => handleDownloadPaymentPdf(payment)}
                              title="Download PDF"
                              data-testid={`download-payment-${payment.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {!payment.receipt_sent_at ? (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-7 text-xs" 
                                onClick={() => openPaymentEmailModal(payment)}
                                disabled={saving}
                                data-testid={`send-payment-${payment.id}`}
                              >
                                Send
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 text-xs text-gray-500" 
                                onClick={() => openPaymentEmailModal(payment)}
                                disabled={saving}
                                title="Resend receipt email"
                                data-testid={`send-payment-${payment.id}`}
                              >
                                Resend
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Void & Re-issue Actions */}
              {order.invoice_number && (
                <div className="mt-3 pt-3 border-t">
                  {isInvoiceVoid && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="w-full text-xs"
                      onClick={handleReissueInvoice} 
                      disabled={saving}
                    >
                      <FileText className="w-3 h-3 mr-1" /> Re-issue Invoice
                    </Button>
                  )}
                  {isAdmin && ['ISSUED', 'SENT', 'issued', 'sent'].includes(order.invoice_status) && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 text-xs"
                      onClick={() => setShowVoidModal(true)}
                      disabled={saving}
                    >
                      <XCircle className="w-3 h-3 mr-1" /> Void Invoice
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            {/* Payment Reminders Card - Compact version */}
            {order.invoice_number && (
              <div className="bg-white rounded-xl border p-6" data-testid="payment-reminders-card">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" /> Reminders
                </h2>
                
                {/* Paid/Void status message */}
                {(isInvoicePaid || isInvoiceVoid) ? (
                  <div className="text-center py-4 text-gray-400">
                    <CheckCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Invoice is {isInvoicePaid ? 'paid' : 'void'}</p>
                  </div>
                ) : (
                  <>
                    {/* Compact checkboxes */}
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Before Due</p>
                        <div className="flex flex-wrap gap-2">
                          {[7, 3, 1].map(days => (
                            <label 
                              key={`before-${days}`} 
                              className="flex items-center gap-1.5 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={reminderSchedule.before_due.includes(days)}
                                onChange={() => toggleReminderDay('before_due', days)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5"
                                data-testid={`reminder-before-${days}`}
                              />
                              <span className="text-xs text-gray-600">{days}d</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">After Due</p>
                        <div className="flex flex-wrap gap-2">
                          {[1, 7, 14].map(days => (
                            <label 
                              key={`after-${days}`} 
                              className="flex items-center gap-1.5 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={reminderSchedule.after_due.includes(days)}
                                onChange={() => toggleReminderDay('after_due', days)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5"
                                data-testid={`reminder-after-${days}`}
                              />
                              <span className="text-xs text-gray-600">{days}d</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Save button */}
                    <Button 
                      onClick={handleSaveReminders}
                      disabled={savingReminders}
                      size="sm"
                      className="w-full mt-4"
                      data-testid="save-reminders-btn"
                    >
                      {savingReminders ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Save className="w-3 h-3 mr-1" />
                      )}
                      Save
                    </Button>
                  </>
                )}
              </div>
            )}
            
            {/* Activity Panel */}
            <EntityActivityPanel entityType="ORDER" entityId={order.order_id} title="Activity" />
            
          </div>
          {/* END RIGHT COLUMN */}
          
        </div>
      </div>
      
      {/* Ops Status Modal */}
      <Dialog open={showOpsStatusModal} onOpenChange={setShowOpsStatusModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Ops Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {Object.entries(OPS_STATUS_CONFIG).map(([value, config]) => (
              <button
                key={value}
                onClick={() => handleOpsStatusChange(value)}
                className={`w-full p-3 rounded-lg border text-left transition-all hover:border-gray-300
                  ${order.ops_status === value ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                `}
              >
                <span className={`inline-flex items-center gap-2 px-2 py-1 rounded text-sm font-medium ${config.color}`}>
                  {config.icon && <config.icon className="w-3.5 h-3.5" />}
                  {config.label}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Fulfillment Status Modal */}
      <Dialog open={showFulfillmentModal} onOpenChange={setShowFulfillmentModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Fulfillment Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {Object.entries(FULFILLMENT_STATUS_CONFIG).map(([value, config]) => (
              <button
                key={value}
                onClick={() => handleFulfillmentChange(value)}
                className={`w-full p-3 rounded-lg border text-left transition-all hover:border-gray-300
                  ${order.fulfillment_status === value ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                `}
              >
                <span className={`inline-flex items-center gap-2 px-2 py-1 rounded text-sm font-medium ${config.color}`}>
                  {config.icon && <config.icon className="w-3.5 h-3.5" />}
                  {config.label}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Tracking Number Modal (for SHIPPED status) */}
      <Dialog open={showTrackingModal} onOpenChange={(open) => {
        if (!open) {
          setShowTrackingModal(false);
          setPendingShipmentStatus(null);
          setTrackingNumberInput('');
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Add Tracking Number
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-600">
              Enter the tracking number for this shipment (optional).
            </p>
            <div className="space-y-2">
              <Label htmlFor="tracking-number">Tracking Number</Label>
              <Input
                id="tracking-number"
                value={trackingNumberInput}
                onChange={(e) => setTrackingNumberInput(e.target.value)}
                placeholder="e.g., 1Z999AA10123456784"
                data-testid="tracking-number-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={handleTrackingSkip}
              disabled={saving}
              data-testid="tracking-skip-btn"
            >
              {pendingShipmentStatus ? 'Skip' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleTrackingSave}
              disabled={saving || (!pendingShipmentStatus && !trackingNumberInput)}
              data-testid="tracking-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {pendingShipmentStatus 
                ? (trackingNumberInput ? 'Save & Mark Shipped' : 'Mark Shipped')
                : 'Save Tracking #'
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Void Invoice Modal */}
      <Dialog open={showVoidModal} onOpenChange={setShowVoidModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Void Invoice</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              This will void invoice <strong>{order.invoice_number}</strong>. 
              Customer/item fields will become editable again.
            </p>
            <div>
              <Label>Reason for voiding *</Label>
              <Textarea 
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Customer requested changes, pricing error, etc."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoidModal(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleVoidInvoice}
              disabled={saving || !voidReason.trim()}
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Void Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Item Modal */}
      <Dialog open={showAddItemModal} onOpenChange={setShowAddItemModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Product Name *</Label>
              <Input 
                value={newItem.product_name}
                onChange={(e) => setNewItem(prev => ({ ...prev, product_name: e.target.value }))}
                placeholder="Enter product name"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea 
                value={newItem.description || ''}
                onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description (supports multiple lines)"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input 
                  type="number"
                  min="1"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div>
                <Label>Unit Price</Label>
                <Input 
                  type="number"
                  step="0.01"
                  min="0"
                  value={newItem.unit_price}
                  onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemModal(false)}>Cancel</Button>
            <Button onClick={handleAddItem} disabled={!newItem.product_name}>
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Payment Modal */}
      <CreatePaymentModal
        open={showPaymentModal}
        onOpenChange={(isOpen) => {
          setShowPaymentModal(isOpen);
          // Only fetch on close when NOT triggered by onSuccess (to avoid double-fetch)
        }}
        onSuccess={() => {
          setShowPaymentModal(false);
          // Fetch after successful payment to update UI
          fetchOrder();
          fetchPayments();
        }}
        prefillCustomer={order ? { id: order.customer_id || order.account_id, name: order.customer_name } : null}
        prefillInvoice={order?.invoice_number ? {
          id: order.order_id,
          invoice_number: order.invoice_number,
          total_cents: Math.round((order.total || 0) * 100),
          balance_cents: Math.round((order.total || 0) * 100) - (order.amount_paid_cents || 0)
        } : null}
        defaultType="sales"
      />
      
      {/* Unified Send Email Modal */}
      <SendEmailModal
        open={showSendEmailModal}
        onOpenChange={setShowSendEmailModal}
        emailType={sendEmailType}
        order={order}
        payment={selectedPaymentForEmail}
        onSend={handleSendEmail}
        onSuccess={fetchOrder}
      />
      
      {/* Cancel Order Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to cancel order <strong>{order?.order_id}</strong>?
            </p>
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              <strong>Warning:</strong> This will void any issued invoice and quote for this order.
            </p>
            <div>
              <Label htmlFor="cancel-reason">Cancellation Reason <span className="text-red-500">*</span></Label>
              <Textarea
                id="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
                className="mt-1"
                rows={3}
                data-testid="cancel-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Keep Order
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelOrder}
              disabled={cancelling || !cancelReason.trim()}
              data-testid="confirm-cancel-btn"
            >
              {cancelling && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Cancel Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Invoice Preview Modal - Embedded PDF */}
      <Dialog open={showInvoicePreview} onOpenChange={(open) => {
        setShowInvoicePreview(open);
        if (!open && invoicePdfUrl) {
          URL.revokeObjectURL(invoicePdfUrl);
          setInvoicePdfUrl(null);
        }
      }}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 flex flex-col">
          <div className="flex-shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold">Invoice Preview</DialogTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowInvoicePreview(false)} data-testid="close-invoice-preview-btn">
                Close
              </Button>
              <Button
                onClick={() => {
                  // Open PDF in new tab for printing
                  const pdfUrl = orderLifecycleAPI.getInvoicePreviewUrl(orderId);
                  window.open(pdfUrl, '_blank');
                }}
                data-testid="print-invoice-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
          
          {/* PDF Viewer */}
          <div className="flex-1 relative bg-gray-100">
            {invoicePdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                  <span className="text-sm text-gray-600">Loading invoice...</span>
                </div>
              </div>
            )}
            {invoicePdfUrl && (
              <object
                data={invoicePdfUrl}
                type="application/pdf"
                className="w-full h-full"
                data-testid="invoice-pdf-viewer"
              >
                <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                  <FileText className="w-16 h-16 text-amber-500" />
                  <p className="text-center text-gray-600">
                    Your browser cannot display the PDF inline.
                  </p>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => window.open(invoicePdfUrl, '_blank')}>
                      <Eye className="w-4 h-4 mr-2" />
                      Open in New Tab
                    </Button>
                    <Button onClick={() => {
                      const a = document.createElement('a');
                      a.href = invoicePdfUrl;
                      a.download = `Invoice-${order?.invoice_number || 'download'}.pdf`;
                      a.click();
                    }}>
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              </object>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Quote Preview Modal - Embedded PDF */}
      <Dialog open={showQuotePreview} onOpenChange={(open) => {
        setShowQuotePreview(open);
        if (!open && quotePdfUrl) {
          URL.revokeObjectURL(quotePdfUrl);
          setQuotePdfUrl(null);
        }
      }}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 flex flex-col">
          <div className="flex-shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold">Quote Preview</DialogTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowQuotePreview(false)} data-testid="close-quote-preview-btn">
                Close
              </Button>
              <Button
                onClick={() => {
                  // Open PDF in new tab for printing
                  const pdfUrl = orderLifecycleAPI.getQuotePreviewUrl(orderId);
                  window.open(pdfUrl, '_blank');
                }}
                data-testid="print-quote-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
          
          {/* PDF Viewer */}
          <div className="flex-1 relative bg-gray-100">
            {quotePdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                  <span className="text-sm text-gray-600">Loading quote...</span>
                </div>
              </div>
            )}
            {quotePdfUrl && (
              <object
                data={quotePdfUrl}
                type="application/pdf"
                className="w-full h-full"
                data-testid="quote-pdf-viewer"
              >
                <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                  <FileText className="w-16 h-16 text-indigo-500" />
                  <p className="text-center text-gray-600">
                    Your browser cannot display the PDF inline.
                  </p>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => window.open(quotePdfUrl, '_blank')}>
                      <Eye className="w-4 h-4 mr-2" />
                      Open in New Tab
                    </Button>
                    <Button onClick={() => {
                      const a = document.createElement('a');
                      a.href = quotePdfUrl;
                      a.download = `Quote-${order?.quote_number || 'download'}.pdf`;
                      a.click();
                    }}>
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              </object>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Payment Receipt Preview Modal */}
      <Dialog open={showPaymentPreview} onOpenChange={(open) => {
        setShowPaymentPreview(open);
        if (!open && paymentPdfUrl) {
          URL.revokeObjectURL(paymentPdfUrl);
          setPaymentPdfUrl(null);
          setSelectedPaymentForPreview(null);
        }
      }}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 flex flex-col">
          <div className="flex-shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold">
              Payment Receipt - {selectedPaymentForPreview?.payment_number || selectedPaymentForPreview?.id || ''}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  if (selectedPaymentForPreview) {
                    setShowPaymentPreview(false); // Close preview first
                    openPaymentEmailModal(selectedPaymentForPreview);
                  }
                }}
                disabled={saving}
                data-testid="send-receipt-modal-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {selectedPaymentForPreview?.receipt_sent_at ? 'Resend' : 'Send'} Receipt
              </Button>
              <Button variant="outline" onClick={() => setShowPaymentPreview(false)} data-testid="close-payment-preview-btn">
                Close
              </Button>
              <Button
                onClick={() => {
                  // Open PDF in new tab for printing
                  if (paymentPdfUrl) {
                    window.open(paymentPdfUrl, '_blank');
                  }
                }}
                data-testid="print-payment-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
          
          {/* PDF Viewer */}
          <div className="flex-1 relative bg-gray-100">
            {paymentPdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                  <span className="text-sm text-gray-600">Loading receipt...</span>
                </div>
              </div>
            )}
            {paymentPdfUrl && (
              <object
                data={paymentPdfUrl}
                type="application/pdf"
                className="w-full h-full"
                data-testid="payment-pdf-viewer"
              >
                <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                  <DollarSign className="w-16 h-16 text-green-500" />
                  <p className="text-center text-gray-600">
                    Your browser cannot display the PDF inline.
                  </p>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => window.open(paymentPdfUrl, '_blank')}>
                      <Eye className="w-4 h-4 mr-2" />
                      Open in New Tab
                    </Button>
                    <Button onClick={() => {
                      const a = document.createElement('a');
                      a.href = paymentPdfUrl;
                      a.download = `Receipt-${selectedPaymentForPreview?.payment_number || 'download'}.pdf`;
                      a.click();
                    }}>
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              </object>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Work Order Edit Modal */}
      <WorkOrderEditModal
        open={showWorkOrderEditModal}
        onClose={() => {
          setShowWorkOrderEditModal(false);
          setSelectedWorkOrder(null);
        }}
        workOrder={selectedWorkOrder}
        orderId={orderId}
        onSave={async () => {
          // Refresh work orders after save
          try {
            const woResponse = await operationsAPI.getWorkOrders({ order_id: orderId });
            setWorkOrders(woResponse.work_orders || []);
          } catch (e) {
            console.error('Failed to refresh work orders:', e);
          }
          setShowWorkOrderEditModal(false);
          setSelectedWorkOrder(null);
          toast.success('Work order updated successfully');
        }}
      />
      
      {/* Work Order Preview Modal - Embedded HTML */}
      <Dialog open={showWorkOrderPreviewModal} onOpenChange={(open) => {
        setShowWorkOrderPreviewModal(open);
        if (!open) {
          setSelectedWorkOrderForPreview(null);
        }
      }}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 flex flex-col">
          <div className="flex-shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold">
              Work Order Preview - {selectedWorkOrderForPreview?.work_order_number || selectedWorkOrderForPreview?.id}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowWorkOrderPreviewModal(false)} data-testid="close-wo-preview-btn">
                Close
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const woId = selectedWorkOrderForPreview?.id || selectedWorkOrderForPreview?.work_order_id;
                  window.open(`${process.env.REACT_APP_BACKEND_URL}/api/print/work-orders/${woId}/document`, '_blank');
                }}
                data-testid="open-wo-newtab-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in Tab
              </Button>
              <Button
                onClick={() => {
                  const woId = selectedWorkOrderForPreview?.id || selectedWorkOrderForPreview?.work_order_id;
                  const printWindow = window.open(`${process.env.REACT_APP_BACKEND_URL}/api/print/work-orders/${woId}/document`, '_blank');
                  // Trigger print after load
                  if (printWindow) {
                    printWindow.onload = () => printWindow.print();
                  }
                }}
                data-testid="print-wo-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
          
          {/* HTML Preview in iframe */}
          <div className="flex-1 relative bg-gray-100">
            {selectedWorkOrderForPreview && (
              <iframe
                src={`${process.env.REACT_APP_BACKEND_URL}/api/print/work-orders/${selectedWorkOrderForPreview?.id || selectedWorkOrderForPreview?.work_order_id}/document`}
                className="w-full h-full border-0"
                title="Work Order Preview"
                data-testid="wo-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderDetailPageClean;
